import React, { useState, useMemo } from 'react';
import { PreviewGrid } from '../components/PreviewGrid';
import { AdvancedSearchBar } from '../components/AdvancedSearchBar';
import { useGridView } from '../core/hooks/useGridView';
import type { CrudGridProps, RowAction } from './types';

function humanizeWorkflowName(name: string): string {
	return name
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

export function CrudGrid({
	sdk,
	system,
	objectType,
	action,
	from,
	params = {},
	columns,
	searchParams: initialSearchParams = {},
	serverSidePagination = false,
	enableSelection = false,
	multiSelect = false,
	onSelectionChange,
	rowActions = [],
	view: externalView,
	onViewChange: externalViewChange,
	onRowClick,
	data: externalData
}: CrudGridProps) {
	const [sys, obj, act] = from ? from.split('.') : [system, objectType, action];
	
	if (!sys || !obj || !act) {
		throw new Error('CrudGrid requires either "from" prop (system.objectType.action) or system/objectType/action props');
	}
	
	const [searchParams, setSearchParams] = useState(initialSearchParams);
	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const { view: internalView, setView: setInternalView } = useGridView();
	
	const view = externalView ?? internalView;
	const setView = externalViewChange ?? setInternalView;
	
	const finalParams = {
		...params,
		...(serverSidePagination ? {
			offset: pagination.pageSize * pagination.pageIndex,
			page_size: pagination.pageSize
		} : {}),
		...(Object.keys(searchParams).some(k => searchParams[k] && searchParams[k] !== '') ? {
			search: searchParams
		} : {})
	};
	
	const { data: fetchedData, isLoading, refetch } = sdk.crud.useCommand(
		sys,
		obj,
		act,
		finalParams,
		{ enabled: !externalData }
	);
	
	const data = externalData ?? fetchedData;
	
	const autoDetectedColumns = useMemo(() => {
		if (columns) {
			if (typeof columns === 'number') {
				if (data && data.length > 0) {
					return Object.keys(data[0]).slice(0, columns);
				}
				return [];
			}
			return columns;
		}
		
		if (data && data.length > 0) {
			return Object.keys(data[0]).slice(0, 8);
		}
		
		return [];
	}, [data, columns]);
	
	const parsedRowActions = useMemo(() => {
		if (typeof rowActions === 'function') {
			return rowActions;
		}
		
		if (!Array.isArray(rowActions)) {
			return [];
		}
		
		return rowActions.map(ra => {
			if (typeof ra === 'string') {
				const parts = ra.split(':').map(s => s.trim());
				if (parts.length === 2) {
					const [label, workflowName] = parts;
					return {
						label,
						onClick: async (row: any) => {
							await sdk.crud.executeWorkflow(workflowName, row);
							refetch();
						}
					};
				}
				
				return {
					label: ra,
					onClick: async (row: any) => {
						await sdk.crud.executeWorkflow(ra, row);
						refetch();
					}
				};
			}
			
			return ra;
		});
	}, [rowActions, sdk, refetch]);
	
	const handleSearch = (params: Record<string, string>) => {
		setSearchParams(params);
		setPagination(prev => ({ ...prev, pageIndex: 0 }));
	};
	
	const handleSelectionChange = (rows: any[]) => {
		setSelectedRows(rows);
		onSelectionChange?.(rows);
	};
	
	const [actionLoading, setActionLoading] = useState<string>('');
	
	const handleRowAction = async (action: any, row: any) => {
		setActionLoading(action.id || action.label);
		try {
			const actionDef = typeof parsedRowActions === 'function' 
				? parsedRowActions(row).find((a: RowAction) => a.label === action.label)
				: parsedRowActions.find((a: RowAction) => a.label === action.label);
			
			if (actionDef?.confirm) {
				const message = typeof actionDef.confirm === 'string' 
					? actionDef.confirm 
					: `Are you sure you want to ${actionDef.label.toLowerCase()}?`;
				if (!confirm(message)) {
					return;
				}
			}
			
			await action.onClick(row);
			refetch();
		} finally {
			setActionLoading('');
		}
	};
	
	const finalRowActions = typeof parsedRowActions === 'function'
		? undefined
		: parsedRowActions.map((ra: RowAction) => ({
				id: ra.label,
				label: ra.label,
				onClick: ra.onClick
		  }));
	
	const dynamicRowActions = typeof parsedRowActions === 'function'
		? (row: any) => parsedRowActions(row).map((ra: RowAction) => ({
				id: ra.label,
				label: ra.label,
				onClick: ra.onClick
		  }))
		: undefined;
	
	return (
		<div>
			{Object.keys(initialSearchParams).length > 0 && (
				<AdvancedSearchBar
					searchParameters={searchParams}
					onSearch={handleSearch}
					onRefresh={refetch}
					isLoading={isLoading}
					viewSwitcher={
						<button 
							onClick={() => setView(view === 'table' ? 'card' : 'table')}
							style={{
								padding: '6px 12px',
								background: 'var(--input-bg)',
								border: '1px solid var(--border-color)',
								borderRadius: '4px',
								color: 'var(--main-text)',
								cursor: 'pointer',
								fontSize: '14px'
							}}
						>
							{view === 'table' ? '📇 Card View' : '📊 Table View'}
						</button>
					}
				/>
			)}
			
			<PreviewGrid
				columns={autoDetectedColumns}
				data={Array.isArray(data) ? data : (data?.result || [])}
				isLoading={isLoading}
				pagination={pagination}
				onPaginationChange={setPagination}
				serverSidePagination={serverSidePagination}
				rowCount={data?.total_count}
				enableSelection={enableSelection}
				multiRowSelection={multiSelect}
				selectedRows={selectedRows}
				onSelectRows={handleSelectionChange}
				view={view}
				onViewChange={setView}
				rowActions={dynamicRowActions ? undefined : finalRowActions}
				onRowAction={handleRowAction}
				actionLoading={actionLoading}
			/>
		</div>
	);
}

